import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useRef, useState } from "react"

import { useAgentIterationDetailsModel } from "@/components/AgentIteration/AgentIterationDetailsModel.ts"
import { AgentIterationStyles } from "@/components/AgentIteration/AgentIterationStyles.ts"
import { Button } from "@/components/Button/Button.tsx"
import { AgentMessageContainer } from "@/components/Chat/AgentMessageStyles.ts"
import { MessageText } from "@/components/Chat/MessageStyles.ts"
import { StreamedTextBlock } from "@/components/Chat/StreamedTextBlock.tsx"
import { Faded } from "@/components/Faded/Faded.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { ReasoningStep } from "@/components/ReasoningStep/ReasoningStep.tsx"
import { SlidingText } from "@/components/SlidingText/SlidingText.tsx"
import { Squircle } from "@/components/Squircle/Squircle.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import { ChevronRightLight, Icon12 } from "@/icons/Icons.ts"
import { preventTextSelection } from "@/lib/std.ts"
import { Rotate0, Rotate90 } from "@/styles/mixins.ts"
import * as textStyles from "@/styles/textStyles.ts"

import type {
  AiChatMessageAgentIterationStep,
  AiChatMessageContent,
  TID,
  UTaskProcessDetails
} from "@/lib/client/generated/TypeDefinitions.ts"

export const AgentIterationMessage = (props: {
  taskId: string
  messageId: string
  content: AiChatMessageContent.AgentIteration
  isNew: boolean
  isLast: boolean
  isActive: boolean
  isAgentRunning: boolean
  retryTaskProcess: () => Promise<void>
  continueTaskProcess: () => Promise<void>
  runningProcess: UTaskProcessDetails | null
}) => {
  const isUnexpectedError = props.content.result?.className === "UnexpectedError" || props.content.result?.className === "LLMProviderError"
  const agentEndMessage = () => {
    return props.content.agentEndMessage && (
      <StreamedTextBlock
        text={props.content.agentEndMessage}
        isStreamCompleted={props.content.agentEndMessageStatus !== "InProgress"}
        isStreamed={props.isLast}
        isNew={props.isNew} />
    )
  }

  return (
    <AgentMessageContainer isActive={props.isActive}>
      <MessageText>
        <Vertical gap="24px">
          {props.content.agentStartMessage && (
            <StreamedTextBlock
              text={props.content.agentStartMessage}
              isStreamCompleted={!!props.content.title || !!props.content.agentEndMessage || props.content.agentStartMessageStatus !== "InProgress"}
              isNew={props.isNew} isStreamed={props.isLast} />
          )}

          {isUnexpectedError && agentEndMessage()}

          <AgentIterationStatus
            messageId={props.messageId as TID}
            content={props.content}
            isRunning={props.isLast && props.isAgentRunning}
            isNew={props.isNew}
            isLast={props.isLast && !props.content.agentEndMessage}
            isActive={props.isActive}
            canRetry={props.isLast && !!props.content.result?.canRestart && !props.isAgentRunning}
            canContinue={props.isLast && !!props.content.result?.canContinue && !props.isAgentRunning}
            taskId={props.taskId}
            retry={props.retryTaskProcess}
            continue={props.continueTaskProcess}
          />

          {!isUnexpectedError && agentEndMessage()}
        </Vertical>
      </MessageText>
    </AgentMessageContainer>
  )
}

const AgentIterationStatus = (props: {
  messageId: TID,
  content: AiChatMessageContent.AgentIteration
  isRunning: boolean
  isNew: boolean
  isLast: boolean
  isActive: boolean
  canRetry: boolean,
  canContinue: boolean,
  taskId: string,
  retry: () => void
  continue: () => void
}) => {
  const steps = [...(props.content.steps ?? [])]

  const [streamCommandOutput, setStreamCommandOutput] = useState(props.isNew)

  const isExpandable = steps.length > 0 || !!props.content.resultStep
  const [expanded, setExpanded] = useState(props.isRunning)

  const [visible, setVisible] = useState(true)
  const [pendingRetry, setPendingRetry] = useState(false)
  const devMode = useDevMode()

  const subtitle = props.content.subtitleStep

  useEffect(() => {
    if (!expanded) {
      setStreamCommandOutput(false)
    }
  }, [expanded])

  // We need to reset the visible state when the content changes
  useEffect(() => {
    if (!visible) {
      return () => setVisible(true)
    } else {
      return () => {}
    }
  }, [props.content, visible])

  const isActive = props.isRunning && props.isLast && props.isActive
  const isActiveRef = useRef<boolean | null>(null)
  const isActiveOld = isActiveRef.current
  isActiveRef.current = isActive
  if (isActiveOld != null && !isActive && isActiveOld) {
    setExpanded(false)
  } else if (isActiveOld == null && isActive) {
    setExpanded(true)
  }

  if (!props.content.title && !props.canRetry && !(devMode && props.canContinue)) {
    return null
  }

  return (
    <AnimatePresence onExitComplete={() => {
      if (pendingRetry) {
        props.retry()
      }
    }}>
      {visible && (
        <motion.div
          key="squircle"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <AgentIterationSquircle canRetry={props.canRetry || (devMode && props.canContinue)}>
            <Vertical
              flex="1 1 auto"
              onClick={() => setExpanded(!expanded)}
              onMouseDown={preventTextSelection}
              className={cx(AgentIterationStyles.header, isExpandable ? Clickable : undefined)}
            >
              <Horizontal gap="4px" alignItems="center" flex="0 0 auto">
                {isExpandable && (expanded
                    ? <ChevronRightLight className={cx(Rotate90, Icon12)} color="var(--secondary-text-color)" />
                    : <ChevronRightLight className={cx(Rotate0, Icon12)} color="var(--secondary-text-color)" />
                )}

                <AgentIterationTitle>
                  {props.content.title}
                </AgentIterationTitle>
              </Horizontal>

              {subtitle &&
                <AgentIterationSubtitle value={subtitle} isExpandable={isExpandable} isExpanded={expanded} />
              }

              {!subtitle && props.content.message && (
                <SlidingStatusContainer
                  size={8}
                  backgroundColor="transparent"
                  isExpandable={isExpandable}
                  className={AgentIterationStyles.secondLevelText}
                >
                  <SlidingText value={props.content.message} offset={24} />
                </SlidingStatusContainer>
              )}
            </Vertical>

            {expanded && isExpandable && (
              <AgentIterationDetails
                chatMessageId={props.messageId}
                steps={steps}
                resultStep={props.content.resultStep}
                isRunning={props.isRunning}
                isNew={streamCommandOutput}
                isLast={props.isLast}
                taskId={props.taskId}
              />
            )}

            {(props.canRetry || (devMode && props.canContinue)) && (
              <ButtonsContainer>
                {(devMode && props.canContinue) ? (
                  <ContinueButton data-testid="agent-continue-button" kind="secondary" onClick={() => {
                    props.continue()
                  }}>
                    Continue
                  </ContinueButton>
                ) : props.canRetry ? (
                  <RetryButton data-testid="agent-retry-button" kind={devMode ? "secondary" : "danger"} onClick={() => {
                    setVisible(false)
                    setPendingRetry(true)
                  }}>
                    Retry
                  </RetryButton>
                ) : null}
              </ButtonsContainer>
            )}
          </AgentIterationSquircle>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const AgentIterationSubtitle = (props: {
  value: AiChatMessageAgentIterationStep | null
  isExpandable: boolean
  isExpanded: boolean
}) => {
  if (!props.value) {
    return null
  }

  const message = props.value?.result?.message

  if (!message || props.isExpanded && props.isExpandable) {
    return null
  }

  return (
    <SlidingStatusContainer
      size={8}
      backgroundColor="transparent"
      isExpandable={props.isExpandable}
      className={cx(props.value.inProgress ? AgentIterationStyles.secondLevelText_Loading : null, AgentIterationStyles.secondLevelText)}
    >
      <SlidingText value={message} offset={24} icon={props.value.result?.icon || undefined} />
    </SlidingStatusContainer>
  )
}

const AgentIterationDetails = ({ chatMessageId, steps, resultStep, isRunning, taskId }: {
  chatMessageId: TID,
  steps: AiChatMessageAgentIterationStep[]
  resultStep?: AiChatMessageAgentIterationStep | null
  isRunning: boolean
  isNew: boolean
  isLast: boolean,
  taskId: string
}) => {
  const { hasSeparatedResultStep } = useAgentIterationDetailsModel({
    chatMessageId,
    steps,
    resultStep,
  })

  return (
    <ExpandableSection className={hasSeparatedResultStep ? ExpandableSection_SeparatedResultStep : undefined}>
      {steps.map((it, index) => (
        <ReasoningStep step={it} key={"step-" + index} isRunning={isRunning} taskId={taskId} />
      ))}

      {resultStep &&
        <ReasoningStep step={resultStep} key={"step-" + steps.length} isRunning={isRunning} taskId={taskId} />
      }
    </ExpandableSection>
  )
}

const ButtonsContainer = styled(Horizontal)`
  margin: 12px 16px 0;
  gap: 8px;
  align-items: flex-start;
`

const RetryButton = styled(Button)``

const ContinueButton = styled(Button)``

const ExpandableSection = styled(Vertical)`
  padding: 0 16px;
`

const ExpandableSection_SeparatedResultStep = css`
  > :last-child {
    margin-top: 16px;
  }
`

const AgentIterationTitle = styled(Horizontal)<{ isError?: boolean }>`
  align-items: center;
  gap: 4px;

  font-weight: 400;
  ${textStyles.smallFontSize};

  color: ${props => props.isError ? "var(--error-color)" : "inherit"};
`

const SlidingStatusContainer = styled(Faded)<{ isExpandable: boolean }>`
  ${textStyles.smallestFontSize};

  overflow: hidden;

  max-width: 100%;
  min-height: 24px;
  max-height: 24px;

  padding-left: ${props => props.isExpandable ? "16px" : "0"};

  line-height: 24px;
`

const AgentIterationSquircle = styled(Squircle)<{ canRetry: boolean }>`
  display: flex;
  flex-direction: column;

  overflow: hidden;

  max-width: 80%;
  min-width: 100%;
  padding: 8px 0 ${props => props.canRetry ? "16px" : "8px"};

  --squircle-border-radius: var(--border-radius-16);
  --squircle-outline: 1px;
  --squircle-fill: var(--separator-color);
`

const Clickable = css`
  cursor: pointer;
`
